import {
	createContext,
	useContext,
	useMemo,
	FC,
	ReactNode,
	useState,
	useCallback,
	useEffect,
} from 'react';
import {
	Application,
	Feedback,
	FunctionaryFeedbackRequest,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { submitFeedbackFromProposer } from '../../../service/VolunteerApplicationService';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { useAuth } from '../../../hooks/useAuth';
import { INTERVIEW_LANDING } from '../../../shared/Routes';
import { useInterviewWizard } from '../useInterviewWizard';
import { Alert } from 'antd';
import { useRedirectToLogin } from '../../../hooks/useRedirectToLogin';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleFilled } from '@ant-design/icons';

export interface ApplicantAnswersReviewContextType {
	eligibilityCriteria: Feedback;
	setEligibilityCriteria: (feedback: Feedback) => void;
	inspirationAndWillingness: Feedback;
	setInspirationAndWillingness: (feedback: Feedback) => void;
	practice: Feedback;
	setPractice: (feedback: Feedback) => void;
	characterAndHabits: Feedback;
	setCharacterAndHabits: (feedback: Feedback) => void;
	availabilityAfterProgram: Feedback;
	setAvailabilityAfterProgram: (feedback: Feedback) => void;
	support: Feedback;
	setSupport: (feedback: Feedback) => void;
	understandingOfHeartfulness: Feedback;
	setUnderstandingOfHeartfulness: (feedback: Feedback) => void;
	dietaryHabitsAndHealth: Feedback;
	setDietaryHabitsAndHealth: (feedback: Feedback) => void;
	application: Partial<Application>;
	onSubmitClick: () => void;
	onCancelClick: () => void;
}

interface Props {
	children: ReactNode;
}

const ApplicantAnswersReviewContext =
	createContext<ApplicantAnswersReviewContextType>({
		eligibilityCriteria: {},
		setEligibilityCriteria: () => {},
		inspirationAndWillingness: {},
		setInspirationAndWillingness: () => {},
		practice: {},
		setPractice: () => {},
		characterAndHabits: {},
		setCharacterAndHabits: () => {},
		availabilityAfterProgram: {},
		setAvailabilityAfterProgram: () => {},
		support: {},
		setSupport: () => {},
		understandingOfHeartfulness: {},
		setUnderstandingOfHeartfulness: () => {},
		dietaryHabitsAndHealth: {},
		setDietaryHabitsAndHealth: () => {},
		application: {},
		onSubmitClick: () => {},
		onCancelClick: () => {},
	});

export const ApplicantAnswersReviewProvider: FC<Props> = ({
	children,
}: Props) => {
	const { srcmId } = useAuth();
	const { t } = useTranslation();
	const { showToastMessage } = useToastMessage();
	const [eligibilityCriteria, setEligibilityCriteria] = useState<Feedback>({});
	const [inspirationAndWillingness, setInspirationAndWillingness] =
		useState<Feedback>({});
	const [practice, setPractice] = useState<Feedback>({});
	const [characterAndHabits, setCharacterAndHabits] = useState<Feedback>({});
	const [availabilityAfterProgram, setAvailabilityAfterProgram] =
		useState<Feedback>({});
	const [support, setSupport] = useState<Feedback>({});
	const [understandingOfHeartfulness, setUnderstandingOfHeartfulness] =
		useState<Feedback>({});
	const [dietaryHabitsAndHealth, setDietaryHabitsAndHealth] =
		useState<Feedback>({});
	const [application, setApplication] = useState<Partial<Application>>({});
	const {
		applicationId,
		navigateWithApplicationId,
		fetchApplication: callFetchApplication,
	} = useApplicationId();
	const { confirmAndSave, onCancelClick, callApi } = useInterviewWizard();
	const { role } = useRedirectToLogin();
	const { infoPopUpAction } = useInfoPopup();

	const saveFeedback = useCallback(async () => {
		const request: FunctionaryFeedbackRequest = {
			applicationId,
			categoryReviewFeedback: {
				availabilityAfterProgram,
				characterAndHabits,
				eligibilityCriteria,
				inspirationAndWillingness,
				practice,
				support,
				understandingOfHeartfulness,
				dietaryHabitsAndHealth,
			},
			feedbackCategory: 'APPLICANT_ANSWERS_REVIEW',
			functionarySrcmId: srcmId as string,
		};

		try {
			await callApi(
				() => submitFeedbackFromProposer(request),
				'errorOccurredWhileSavingFeedback'
			);
			infoPopUpAction(
				'savedSuccessfully',
				'',
				() => {
					navigateWithApplicationId(INTERVIEW_LANDING);
				},
				ConfirmTick
			);
		} catch (e) {
			/* handled inside confirmAndSave */
		}
	}, [
		applicationId,
		availabilityAfterProgram,
		callApi,
		characterAndHabits,
		dietaryHabitsAndHealth,
		eligibilityCriteria,
		inspirationAndWillingness,
		navigateWithApplicationId,
		practice,
		srcmId,
		support,
		understandingOfHeartfulness,
		infoPopUpAction,
	]);

	const onSubmitClick = useCallback(() => {
		confirmAndSave(saveFeedback);
	}, [confirmAndSave, saveFeedback]);

	const value = useMemo<ApplicantAnswersReviewContextType>(() => {
		return {
			eligibilityCriteria,
			setEligibilityCriteria,
			inspirationAndWillingness,
			setInspirationAndWillingness,
			practice,
			setPractice,
			characterAndHabits,
			setCharacterAndHabits,
			availabilityAfterProgram,
			setAvailabilityAfterProgram,
			support,
			setSupport,
			understandingOfHeartfulness,
			setUnderstandingOfHeartfulness,
			dietaryHabitsAndHealth,
			setDietaryHabitsAndHealth,
			application,
			onSubmitClick,
			onCancelClick,
		};
	}, [
		eligibilityCriteria,
		inspirationAndWillingness,
		practice,
		characterAndHabits,
		availabilityAfterProgram,
		support,
		understandingOfHeartfulness,
		dietaryHabitsAndHealth,
		application,
		onSubmitClick,
		onCancelClick,
	]);

	const fetchApplication = useCallback(async () => {
		if (!applicationId) {
			showToastMessage('error', 'applicationId is missing');
		}

		try {
			const fetchedApplication = await callFetchApplication();

			if (
				!eligibilityCriteria ||
				Object.keys(eligibilityCriteria).length === 0
			) {
				setEligibilityCriteria(
					fetchedApplication.answersReviewFeedback?.eligibilityCriteria ?? {}
				);
			}

			if (
				!inspirationAndWillingness ||
				Object.keys(inspirationAndWillingness).length === 0
			) {
				setInspirationAndWillingness(
					fetchedApplication.answersReviewFeedback?.inspirationAndWillingness ??
						{}
				);
			}

			if (!practice || Object.keys(practice).length === 0) {
				setPractice(fetchedApplication.answersReviewFeedback?.practice ?? {});
			}

			if (
				!availabilityAfterProgram ||
				Object.keys(availabilityAfterProgram).length === 0
			) {
				setAvailabilityAfterProgram(
					fetchedApplication.answersReviewFeedback?.availabilityAfterProgram ??
						{}
				);
			}

			if (!characterAndHabits || Object.keys(characterAndHabits).length === 0) {
				setCharacterAndHabits(
					fetchedApplication.answersReviewFeedback?.characterAndHabits ?? {}
				);
			}

			if (!support || Object.keys(support).length === 0) {
				setSupport(fetchedApplication.answersReviewFeedback?.support ?? {});
			}

			if (
				!understandingOfHeartfulness ||
				Object.keys(understandingOfHeartfulness).length === 0
			) {
				setUnderstandingOfHeartfulness(
					fetchedApplication.answersReviewFeedback
						?.understandingOfHeartfulness ?? {}
				);
			}

			if (
				!dietaryHabitsAndHealth ||
				Object.keys(dietaryHabitsAndHealth).length === 0
			) {
				setDietaryHabitsAndHealth(
					fetchedApplication.answersReviewFeedback?.dietaryHabitsAndHealth ?? {}
				);
			}

			setApplication(fetchedApplication);
		} catch (e) {
			//
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [applicationId, callFetchApplication, showToastMessage]);

	useEffect(() => {
		void fetchApplication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ApplicantAnswersReviewContext.Provider value={value}>
			{role === UserRole.ADMIN && (
				<Alert
					message={
						<div className="text-center w-full">
							<ExclamationCircleFilled className="text-[#faad14]" />{' '}
							{t('adminsAreNotAllowed')}
						</div>
					}
					type="warning"
				/>
			)}
			{application?.isApprover && (
				<Alert
					message={
						<div className="text-center w-full">
							<ExclamationCircleFilled className="text-[#faad14]" />{' '}
							{t('approversAreNotAllowed')}
						</div>
					}
					type="warning"
				/>
			)}
			{role === UserRole.ZC && (
				<Alert
					message={
						<div className="text-center w-full">
							<ExclamationCircleFilled className="text-[#faad14]" />{' '}
							{t('zcIsNotAllowed')}
						</div>
					}
					type="warning"
				/>
			)}
			{role === UserRole.CARE_TEAM && (
				<Alert
					message={
						<div className="text-center w-full">
							<ExclamationCircleFilled className="text-[#faad14]" />{' '}
							{t('careTeamIsNotAllowed')}
						</div>
					}
					type="warning"
				/>
			)}
			{children}
		</ApplicantAnswersReviewContext.Provider>
	);
};

export const useApplicantAnswersReview = () => {
	return useContext(ApplicantAnswersReviewContext);
};
